import { Avatar, AvatarGroup, Stack, Tooltip, Typography } from '@mui/material'

import type React from 'react'
import { useTranslation } from 'react-i18next'

import { buildParticipantList, getShortName } from './avatar'
import { ParticipantAvatar } from './participant-avatar'

const MAX_AVATARS = 5

interface ParticipantStripProps {
  ownerUserId: number | null
  ownerName: string
  contributors: { userId: number; name: string }[]
  currentUserId?: string
}

/**
 * Who's in a team order — the owner plus everyone with an attributed line, deduped by userId
 * (the backend derives contributors from item attribution, so the owner appears there too once
 * they add their own items). Hidden until a non-owner has contributed: a fresh group basket the
 * owner is filling alone reads like a standard draft.
 */
export const ParticipantStrip: React.FC<ParticipantStripProps> = ({
  ownerUserId,
  ownerName,
  contributors,
  currentUserId,
}) => {
  const { t } = useTranslation()

  const tooltipLabel = (participant: { userId: string; name: string }) => {
    const shortName = getShortName(participant.name)
    return currentUserId != null && participant.userId === currentUserId
      ? t('marketplace:participantYou', { name: shortName })
      : shortName
  }

  const hasNonOwnerContributor = contributors.some(
    (contributor) => String(contributor.userId) !== String(ownerUserId),
  )
  if (!hasNonOwnerContributor) return null

  const participants = buildParticipantList(ownerUserId, ownerName, contributors)

  // Hand-rolled overflow instead of AvatarGroup's max: the built-in surplus avatar can't carry
  // a Tooltip, which would leave hidden participants unnameable (same trade-off as canteen's
  // MenuHeaderV2/menuProviders.tsx). A string title keeps MUI emitting a real aria-label.
  const showSurplus = participants.length > MAX_AVATARS
  const visibleParticipants = showSurplus ? participants.slice(0, MAX_AVATARS - 1) : participants
  const hiddenParticipants = participants.slice(visibleParticipants.length)

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={1}
      data-cy="cateringTeamOrderParticipantStrip"
      sx={{ padding: '12px 12px 10px', borderBottom: '1px solid', borderColor: 'divider' }}>
      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
        {t('marketplace:peopleInThisOrder', { count: participants.length })}
      </Typography>
      <AvatarGroup
        sx={{
          '& .MuiAvatar-root': {
            width: 28,
            height: 28,
            fontSize: 12,
            transition: 'border-color .15s',
          },
          '& .MuiAvatar-root:hover': { borderColor: 'secondary.main' },
        }}>
        {visibleParticipants.map((participant) => (
          <Tooltip key={participant.userId} title={tooltipLabel(participant)}>
            <ParticipantAvatar userId={participant.userId} name={participant.name} />
          </Tooltip>
        ))}
        {showSurplus ? (
          <Tooltip title={hiddenParticipants.map(tooltipLabel).join('\n')}>
            <Avatar>{`+${hiddenParticipants.length}`}</Avatar>
          </Tooltip>
        ) : null}
      </AvatarGroup>
    </Stack>
  )
}
